import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";
import { clientsApi } from "./clients.api";
import { leadScoringApi } from "./leadScoring.api";

// Lead-tracking writes update fields on the client record itself
// (isContacted, source, isConverted, conversionValue, etc.) — the
// Clients page, ClientDetail, and Hot Leads widget all read those.
// Without cross-invalidation, marking a lead as contacted here would
// leave the Clients table showing the old "not contacted" state until
// a hard refresh, even though the backend has already updated the row.
const invalidateClientAndScoringCaches = (dispatch) => {
  try {
    dispatch(clientsApi.util.invalidateTags(["Client", "Clients"]));
    dispatch(leadScoringApi.util.invalidateTags(["LeadScores", "HotLeads", "ScoreStats"]));
  } catch {
    // Non-fatal — if either slice isn't mounted we skip silently.
  }
};

export const leadAnalyticsApi = createApi({
  reducerPath: "leadAnalyticsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["LeadAnalytics", "Leads", "SourcePerformance"],
  endpoints: (build) => ({
    // Get comprehensive lead analytics
    getLeadAnalytics: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics",
        method: "GET",
        params,
      }),
      providesTags: ["LeadAnalytics"],
      transformResponse: (response) => response.data || response,
    }),

    // Get leads by source with pagination
    getLeadsBySource: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/leads",
        method: "GET",
        params,
      }),
      providesTags: ["Leads"],
      transformResponse: (response) => ({
        leads: response.data || [],
        pagination: response.pagination || {},
      }),
    }),

    // Get source performance metrics
    getSourcePerformance: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/performance",
        method: "GET",
        params,
      }),
      providesTags: ["SourcePerformance"],
      transformResponse: (response) => response.data || response,
    }),

    // Get conversion funnel data
    getConversionFunnel: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/funnel",
        method: "GET",
        params,
      }),
      providesTags: ["LeadAnalytics"],
      transformResponse: (response) => response.data || response,
    }),

    // Get response time analytics
    getResponseTimeAnalytics: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/response-time",
        method: "GET",
        params,
      }),
      providesTags: ["LeadAnalytics"],
      transformResponse: (response) => response.data || response,
    }),

    // Get ROI by source
    getSourceROI: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/roi",
        method: "GET",
        params,
      }),
      providesTags: ["LeadAnalytics"],
      transformResponse: (response) => response.data || response,
    }),

    // Get open house analytics
    getOpenHouseAnalytics: build.query({
      query: (params = {}) => ({
        url: "/lead-analytics/open-house",
        method: "GET",
        params,
      }),
      providesTags: ["LeadAnalytics"],
      transformResponse: (response) => response.data || response,
    }),

    // Update client lead tracking
    updateLeadTracking: build.mutation({
      query: ({ clientId, ...body }) => ({
        url: `/lead-analytics/client/${clientId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["LeadAnalytics", "Leads", "SourcePerformance"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateClientAndScoringCaches(dispatch); } catch {}
      },
    }),

    // Bulk update lead sources
    bulkUpdateLeadSources: build.mutation({
      query: (body) => ({
        url: "/lead-analytics/bulk-update-source",
        method: "POST",
        body,
      }),
      invalidatesTags: ["LeadAnalytics", "Leads", "SourcePerformance"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateClientAndScoringCaches(dispatch); } catch {}
      },
    }),
  }),
});

export const {
  useGetLeadAnalyticsQuery,
  useGetLeadsBySourceQuery,
  useGetSourcePerformanceQuery,
  useGetConversionFunnelQuery,
  useGetResponseTimeAnalyticsQuery,
  useGetSourceROIQuery,
  useGetOpenHouseAnalyticsQuery,
  useUpdateLeadTrackingMutation,
  useBulkUpdateLeadSourcesMutation,
} = leadAnalyticsApi;
